import React from 'react';
import Modal from '@/components/Modal';
import Button from '@/components/Button';
import { alreadyActionedModal } from '@/utils/translations';
import {
  AlreadyActionedOutcomeEnum,
  TAlreadyActionedOutcome
} from '../data/alreadyActionedOutcome';

interface AlreadyActionedModalProps {
  isOpen: boolean;
  onClose: () => void;
  outcome: TAlreadyActionedOutcome | null;
  /** Selects "This payment" vs "This batch payment" in the body copy. */
  isBatch: boolean;
}

const CONFIG: Record<
  TAlreadyActionedOutcome,
  { title: string; body: (subject: string) => string }
> = {
  [AlreadyActionedOutcomeEnum.APPROVED]: {
    title: alreadyActionedModal.approvedTitle,
    body: alreadyActionedModal.approvedBody
  },
  [AlreadyActionedOutcomeEnum.APPROVED_BY_YOU]: {
    title: alreadyActionedModal.approvedByYouTitle,
    body: alreadyActionedModal.approvedByYouBody
  },
  [AlreadyActionedOutcomeEnum.APPROVED_PENDING_BY_YOU]: {
    title: alreadyActionedModal.approvedPendingByYouTitle,
    body: alreadyActionedModal.approvedPendingByYouBody
  },
  [AlreadyActionedOutcomeEnum.REJECTED]: {
    title: alreadyActionedModal.rejectedTitle,
    body: alreadyActionedModal.rejectedBody
  },
  [AlreadyActionedOutcomeEnum.REJECTED_BY_YOU]: {
    title: alreadyActionedModal.rejectedByYouTitle,
    body: alreadyActionedModal.rejectedByYouBody
  },
  [AlreadyActionedOutcomeEnum.EXPIRED]: {
    title: alreadyActionedModal.expiredTitle,
    body: alreadyActionedModal.expiredBody
  }
};

/**
 * Shown in place of the generic error banner when approve()/confirmReject() hits a 4100/4103
 * conflict (someone - possibly this same customer, from another device - already actioned this
 * payment/batch since the screen last checked). Rather than leaving the user to guess why
 * Approve/Reject just silently vanished, this spells out exactly what happened before onClose
 * takes them back to the payments list - same closing behaviour as the terminal outcome screens
 * (see PaymentOutcomeResult) this sits alongside.
 */
const AlreadyActionedModal: React.FC<AlreadyActionedModalProps> = ({
  isOpen,
  onClose,
  outcome,
  isBatch
}) => {
  if (!outcome) return null;

  const config = CONFIG[outcome];
  const subject = isBatch ? alreadyActionedModal.batchSubject : alreadyActionedModal.paymentSubject;

  return (
    <Modal isOpen={isOpen} onClose={onClose} headerText={config.title} headerVariant="xxl-medium">
      <div className="flex flex-col gap-y-6 py-4">
        <p className="text-sm font-medium text-emerge-navy">{config.body(subject)}</p>

        <div className="flex justify-center">
          <Button size="pill" className="w-[140px]" onClick={onClose}>
            {alreadyActionedModal.gotItButton}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AlreadyActionedModal;
